import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { EnvelopeSimple, Trash, PaperPlaneTilt, CheckCircle, XCircle, User as UserIcon, Note } from "@phosphor-icons/react";

export default function TaxAdvisorsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", note: "", monthly: true });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/tax-advisors"); setItems(r.data.recipients || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.email) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.post("/tax-advisors", form);
      setForm({ email: "", name: "", note: "", monthly: true });
      setMsg("Empfänger hinzugefügt");
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const toggleActive = async (rec) => {
    try { await api.patch(`/tax-advisors/${rec.recipient_id}`, { active: !rec.active }); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const toggleMonthly = async (rec) => {
    try { await api.patch(`/tax-advisors/${rec.recipient_id}`, { monthly: !rec.monthly }); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const sendNow = async (rec) => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.post(`/tax-advisors/${rec.recipient_id}/send-now`);
      setMsg(`Export an ${rec.email} versendet`);
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const remove = async (rec) => {
    if (!confirm(`Empfänger ${rec.email} wirklich entfernen?`)) return;
    try { await api.delete(`/tax-advisors/${rec.recipient_id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <section className="bg-white border border-black/10 rounded-lg p-5 space-y-4" data-testid="tax-advisors-panel">
      <div>
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
          <EnvelopeSimple size={18} weight="bold" /> Steuerberater-Zugang
        </h3>
        <p className="text-xs text-zinc-500 mt-1">
          Automatischer Controlling-CSV-Versand am 3. jedes Monats an eingetragene Empfänger. Enthält KPIs, Rechnungen, Gutschriften und Kunden-Aggregate.
        </p>
      </div>

      {/* Add form */}
      <div className="border border-black/10 rounded-md p-3 grid sm:grid-cols-2 gap-2 bg-zinc-50" data-testid="tax-advisor-add-form">
        <input
          placeholder="E-Mail *"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="h-9 px-3 rounded-md border border-black/10 text-sm sm:col-span-1"
          data-testid="tax-advisor-email"
        />
        <input
          placeholder="Name (optional)"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="h-9 px-3 rounded-md border border-black/10 text-sm sm:col-span-1"
          data-testid="tax-advisor-name"
        />
        <input
          placeholder="Notiz (optional)"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="h-9 px-3 rounded-md border border-black/10 text-sm sm:col-span-2"
          data-testid="tax-advisor-note"
        />
        <label className="flex items-center gap-2 text-xs text-zinc-700">
          <input
            type="checkbox"
            checked={form.monthly}
            onChange={(e) => setForm({ ...form, monthly: e.target.checked })}
            data-testid="tax-advisor-monthly"
          />
          Monatlichen Versand aktivieren
        </label>
        <button
          onClick={add}
          disabled={busy || !form.email}
          className="justify-self-end sm:justify-self-end px-4 py-2 rounded-md text-white text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
          data-testid="tax-advisor-add-btn"
        >
          + Empfänger hinzufügen
        </button>
      </div>

      {msg && <div className="text-sm text-emerald-700" data-testid="tax-advisor-msg">✓ {msg}</div>}
      {err && <div className="text-sm text-red-600" data-testid="tax-advisor-err">{err}</div>}

      {/* List */}
      {loading ? (
        <div className="text-xs text-zinc-400 py-3">Lade Empfänger…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-zinc-400 py-4 text-center border border-dashed border-black/10 rounded-md">
          Noch kein Steuerberater eingetragen. Fügen Sie oben eine E-Mail hinzu.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((rec) => (
            <div key={rec.recipient_id} className="flex flex-wrap items-center justify-between gap-3 p-3 border border-black/10 rounded-md" data-testid={`tax-advisor-row-${rec.recipient_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <UserIcon size={13} weight="bold" className="text-zinc-500" />
                  {rec.name || <span className="text-zinc-400 italic">Ohne Namen</span>}
                  {!rec.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-700 uppercase font-bold">inaktiv</span>}
                </div>
                <div className="text-xs text-zinc-500 font-mono">{rec.email}</div>
                {rec.note && <div className="text-xs text-zinc-400 flex items-center gap-1 mt-1"><Note size={11} weight="bold" /> {rec.note}</div>}
                {rec.last_sent_at && (
                  <div className="text-[10px] text-zinc-400 mt-1">
                    Letzter Versand: {new Date(rec.last_sent_at).toLocaleString("de-DE")}{" "}
                    {rec.last_sent_status?.startsWith("error") ? (
                      <span className="text-red-600">— {rec.last_sent_status}</span>
                    ) : (
                      <span className="text-emerald-700">— ok</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[11px] flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={rec.monthly} onChange={() => toggleMonthly(rec)} data-testid={`toggle-monthly-${rec.recipient_id}`} />
                  monatlich
                </label>
                <label className="text-[11px] flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={rec.active} onChange={() => toggleActive(rec)} data-testid={`toggle-active-${rec.recipient_id}`} />
                  aktiv
                </label>
                <button
                  onClick={() => sendNow(rec)}
                  disabled={busy}
                  className="text-xs px-3 py-1.5 rounded-full bg-black text-white hover:bg-zinc-800 inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid={`send-now-${rec.recipient_id}`}
                >
                  <PaperPlaneTilt size={11} weight="bold" /> Jetzt senden
                </button>
                <button
                  onClick={() => remove(rec)}
                  className="text-xs w-8 h-8 rounded-full text-red-600 hover:bg-red-50 inline-flex items-center justify-center"
                  data-testid={`remove-${rec.recipient_id}`}
                  title="Entfernen"
                >
                  <Trash size={13} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-zinc-400 border-t border-black/5 pt-3">
        Der automatische Versand läuft jeden Monat am 3. um 07:00 Uhr (Europe/Berlin). Der Export enthält die Kennzahlen des Vormonats.
      </div>
    </section>
  );
}
